import { Box, Button, List, ListItem, Paper, Typography, makeStyles } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HomeLayout from '~/src/HomeLayout/components/HomeLayout';
import useTitle from '~/src/hooks/useTitle';
import useApiClient from '~/src/hooks/useApiClient';

const useStyles = makeStyles(({ spacing, palette }) => ({
  paper: {
    padding: 20,
    marginBottom: spacing(8),
    maxWidth: 700,
    width: '100%',
  },
  stepItem: {
    paddingLeft: 0,
  },
  stepNumber: {
    fontWeight: 700,
    marginRight: spacing(2),
    minWidth: 24,
  },
  downloadButton: {
    marginTop: spacing(4),
  },
  link: {
    color: palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

const renderStepWithLinks = (text: string, linkClass: string) => {
  const parts = text.split(/(<a[^>]*>(?:.*?)<\/a>)/g);
  return parts.map((part, i) => {
    if (part.startsWith('<a')) {
      const hrefMatch = part.match(/href=["']([^"']+)["']/);
      const labelMatch = part.match(/>([^<]+)<\/a/);
      if (hrefMatch && labelMatch) {
        return (
          <Link
            key={i}
            href={hrefMatch[1]}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {labelMatch[1]}
          </Link>
        );
      }
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const Mcp = () => {
  const { t } = useTranslation('mcp');
  const classes = useStyles();
  const apiClient = useApiClient();
  const [downloading, setDownloading] = useState(false);

  useTitle(t('title'));

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const { data } = await apiClient.get('/projects/mcpb-file-download');
      window.open(data.url, '_blank');
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <HomeLayout>
      <Box py={8} px={5} width="100%" minHeight="100px">
        <Paper elevation={2} className={classes.paper}>
          <Typography variant="h5" gutterBottom>
            {t('heading')}
          </Typography>
          <Typography color="textSecondary" paragraph>
            {t('description')}
          </Typography>

          <List>
            {[1, 2, 3, 4, 5, 6, 7].map((step) => (
              <ListItem key={step} className={classes.stepItem}>
                <Typography className={classes.stepNumber}>{step}.</Typography>
                <Typography component="span">
                  {renderStepWithLinks(t(`steps.${step}`), classes.link)}
                </Typography>
              </ListItem>
            ))}
          </List>

          <Box display="flex" justifyContent="flex-start">
            <Button
              variant="contained"
              className={classes.downloadButton}
              onClick={handleDownload}
              disabled={downloading}
            >
              {t('download')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </HomeLayout>
  );
};

export default React.memo(Mcp);
